/**
 * Typed client for the subject-side API.
 *
 * Two endpoints:
 *   - POST /api/subject/activate { code, email } → { token, exp }
 *   - POST /api/subject/turn { inbound } → SSE stream of OutboundEvent
 *
 * Token storage: sessionStorage (NOT localStorage). Ephemeral by design — if the tab
 * closes, the subject re-activates with email + code.
 */

const STORAGE_KEY = "interview_subject_token";

export type InboundEvent =
  | { type: "answer"; questionId: string; text: string; lang: string; durationSeconds?: number; typed?: boolean; audioBlobUrl?: string }
  | { type: "bootstrap" }
  | { type: "user_done" };

export type OutboundEvent =
  | { type: "thinking" }
  | { type: "question_delta"; text: string }
  | { type: "question"; questionId: string; text: string; orderIndex: number }
  | { type: "terminate"; reason: string }
  | { type: "superseded" }
  | { type: "token_expired" }
  | { type: "cost_cap_hit"; kind: string }
  | { type: "error"; message: string };

const apiBase = (): string => {
  const override = import.meta.env.VITE_API_BASE_URL;
  if (override) return override;
  return ""; // same-origin
};

export function getSubjectToken(): string | null {
  return sessionStorage.getItem(STORAGE_KEY);
}

export function setSubjectToken(token: string): void {
  sessionStorage.setItem(STORAGE_KEY, token);
}

export function clearSubjectToken(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

export type ActivateResult =
  | { ok: true; token: string; exp: number }
  | { ok: false; status: number; error: string };

export async function activate(params: { code: string; email: string }): Promise<ActivateResult> {
  const res = await fetch(`${apiBase()}/api/subject/activate`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ code: params.code, email: params.email.trim().toLowerCase() }),
  });

  if (res.ok) {
    const body = (await res.json()) as { token: string; exp: number };
    setSubjectToken(body.token);
    return { ok: true, token: body.token, exp: body.exp };
  }

  let error = "unknown_error";
  try {
    const body = (await res.json()) as { error?: string };
    if (body.error) error = body.error;
  } catch {
    // ignore
  }
  return { ok: false, status: res.status, error };
}

/**
 * Open a per-turn SSE stream. Returns an AsyncIterable<OutboundEvent>. The caller
 * iterates events and calls postTurn again with the next inbound event after each
 * cycle completes (when SSE stream ends).
 */
export async function* postTurn(inbound: InboundEvent): AsyncIterable<OutboundEvent> {
  const token = getSubjectToken();
  if (!token) {
    yield { type: "error", message: "missing_token" };
    return;
  }

  const res = await fetch(`${apiBase()}/api/subject/turn`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ inbound }),
  });

  if (!res.ok) {
    yield { type: "error", message: `http_${res.status}` };
    return;
  }

  if (!res.body) {
    yield { type: "error", message: "no_response_body" };
    return;
  }

  // Parse SSE stream
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += value;

      let eventEndIdx;
      while ((eventEndIdx = buffer.indexOf("\n\n")) !== -1) {
        const chunk = buffer.slice(0, eventEndIdx);
        buffer = buffer.slice(eventEndIdx + 2);
        const event = parseSseChunk(chunk);
        if (event) yield event;
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function parseSseChunk(chunk: string): OutboundEvent | null {
  let data = "";
  for (const line of chunk.split("\n")) {
    if (line.startsWith("data:")) {
      data += line.slice(5).trim();
    }
  }
  if (!data) return null;
  try {
    return JSON.parse(data) as OutboundEvent;
  } catch {
    return null;
  }
}

export async function transcribeAudio(blob: Blob, lang?: string): Promise<{
  ok: true; text: string; language: string; languageMismatch?: boolean
} | { ok: false; status: number; error: string }> {
  const token = getSubjectToken();
  if (!token) return { ok: false, status: 401, error: "missing_token" };

  const form = new FormData();
  form.append("audio", blob, "answer.webm");
  if (lang) form.append("lang", lang);

  const res = await fetch(`${apiBase()}/api/subject/transcribe`, {
    method: "POST",
    headers: { authorization: `Bearer ${token}` },
    body: form,
  });

  if (res.ok) {
    const body = (await res.json()) as { text: string; language: string; languageMismatch?: boolean };
    return { ok: true, ...body };
  }

  let error = "unknown_error";
  try {
    const body = (await res.json()) as { error?: string };
    if (body.error) error = body.error;
  } catch {
    // ignore
  }
  return { ok: false, status: res.status, error };
}

export function ttsUrlFor(questionId: string): string {
  const token = getSubjectToken();
  // TTS is fetched via POST with body containing text. Caller assembles request.
  // This helper exists only for tests that construct URLs.
  return `${apiBase()}/api/subject/tts?questionId=${encodeURIComponent(questionId)}&token=${token ?? ""}`;
}
